import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { S3, WIDGET_ICONS } from "../../../../../../constants";
import useCompareState from "../../../../../../hooks/useCompareState";
import ImageRadio from "../../../../../../parts/inputs/image-radio";
import ImageWidgetUploadInput from "../../../../../../parts/inputs/image-widget-upload";
import { EditWidgetInfoModal } from "../../../../../../parts/modals/EditWidgetInfo";
import SvgIcon from "../../../../../../parts/SvgIcon";
import scriptLoader from "../../../../../../utils/scriptLoader";
import { isPullActiveCoach } from "../../../../../../utils/user-helpers";
import {
  deleteWidgetUtil,
  orderWidgets,
} from "../../../../../../utils/widgets";
import { getUserToRequest } from "../../../../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { EditStore } from "./EditStore";
import WidgetManagerHeader from "./WidgetManagerHeader";

export const StoreWidgetManager = ({ locals, goBack }) => {
  const { t } = useTranslation("coaching-portal");
  const activeWidget = locals.activeWidget;
  const widgetStores = locals.widgetStores;
  const activeUser = locals.activeUser;
  const organizationOwner = locals.organizationOwner;
  const userToRequest = getUserToRequest(activeUser, organizationOwner);

  const [formState, setFormState] = useState({}); //state to control the widget's form
  const [urlState, setUrlState] = useState({ url: "", widget: "", _id: "" }); //state to control the store's edit form
  const [addNew, setAddNew] = useState("addNewButton"); //state to control if the "add new store" section is opened or not
  const [statusState, setStatusState] = useState("initial"); //state to control if the stores are in "change order" mode or not
  const [widgetStoreState, setWidgetStoreState] = useState([]); //state to store the the stores of the widget
  const [editStoreState, setEditStoreState] = useState(""); //state to store the current store to can control the accordions
  const [refreshList, setRefreshList] = useState(false);
  const [editWidgetInfoModalOpened, setEditWidgetInfoModalOpened] =
    useState(false);

  const [changedOrder, setChangedOrder] = useState(false);

  /**
   * Reorder
   */
  useEffect(() => {
    let sortable = null;
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-wrapper",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );

                const ordered = Array.from(items).map((element) => {
                  const imageInput =
                    element.querySelector(".image-section-div");
                  const urlInput = element.querySelector(".url-section-div");
                  const idInput = element.querySelector(".id-section-div");
                  return {
                    image: (imageInput && JSON.parse(imageInput.value)) || "",
                    url: (urlInput && urlInput.href) || "",
                    widget: activeWidget._id,
                    _id: (idInput && idInput.value) || "",
                  };
                });
                setWidgetStoreState(ordered);
                setChangedOrder(true);
                return false;
              },
            }
          );
        }
      );
    }
    return () => {
      if (sortable) sortable.destroy();
    };
  }, []);

  /**
   * Close the "add new store" section
   */
  useEffect(() => {
    if (locals.query.submited) {
      setAddNew("addNewButton");
      setStatusState("initial");
      setEditStoreState("");
    }
  }, [locals.query]);

  /**
   * Set the values to the states
   */
  const [compareBase, setCompareBase, setCurrentCompareState, hasChanged] =
    useCompareState();
  useEffect(() => {
    if (!!activeWidget) {
      const comparingBase = {
        name: activeWidget.name,
        description: activeWidget.description,
        icon: activeWidget.icon,
        pushPermission:
          activeWidget.pushPermission === undefined
            ? true
            : activeWidget.pushPermission,
        pullPermission:
          activeWidget.pullPermission === undefined
            ? true
            : activeWidget.pullPermission,
      };
      setFormState({
        ...comparingBase,
        id: activeWidget._id,
      });
      setCompareBase(comparingBase);
      setCurrentCompareState(comparingBase);
    }
    if (!!widgetStores) {
      setWidgetStoreState(widgetStores);
    }
  }, [activeWidget, widgetStores]);

  const {
    description = "",
    name = "",
    icon = "",
    id = 0,
    pushPermission = true,
    pullPermission = true,
  } = formState;

  /**
   * Validate if the widget's form is valid
   * @returns
   */
  const validationSubmit = () => {
    if (description.length > 0 && name.length > 0 && icon.length > 0) {
      return true;
    }
    return false;
  };

  /**
   * Delete a store
   * @param {*} e
   * @param {*} id
   */
  const handleDelete = (e, id) => {
    if (
      process.browser &&
      confirm(t("widgets.storeManager.confirmDeleteStore"))
    ) {
      e.stopPropagation();
      e.preventDefault();
      submit(`/actions/stores/delete/${id}`, {
        method: "GET",
      });
    }
  };

  const handleInputChange = ({ target }) => {
    const targetName = target?.name.replace("-mobile", "") || target?.name;
    const newState = Object.assign({}, formState, {
      [targetName]: target.value,
    });
    setFormState(newState);
    setCurrentCompareState({
      ...compareBase,
      [targetName]: target.value,
    });
  };

  const handleUrlInputChange = ({ target }) => {
    const newState = Object.assign({}, urlState, {
      [target.name]: target.value,
    });
    setUrlState(newState);
  };

  /**
   * Add a store
   * @param {*} e
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    const body = formState;
    delete body.order;
    delete body.status;
    delete body.type;
    delete body.id;

    submit(`/actions/widgets/save/${id}`, {
      method: "POST",
      body: body,
    });
  };

  /**
   * Control if the "add new store" section is opened or not
   */
  const handleChangeAddNewState = () => {
    setAddNew("addNewForm");
  };

  /**
   * Control if the stores are in "edit mode" or not
   * @param {*} state
   */
  const handleChangeEditState = () => {
    statusState === "initial"
      ? setStatusState("edit")
      : setStatusState("initial");
  };

  /**
   * Delete a widget
   * @param {*} e
   */
  const deleteWidget = (e) => {
    if (!!activeWidget) {
      const { _id } = activeWidget;
      deleteWidgetUtil(e, _id);
    }
  };

  /**
   * Reorder
   * @param {*} e
   */
  const handleSaveReorder = (e) => {
    e.preventDefault();
    const orderedStores = widgetStoreState.map((element) => element._id);

    if (orderedStores.length > 0) {
      setStatusState("initial");
      submit("/actions/stores/reorder", {
        method: "POST",
        body: { orderedStores },
      });
    }
  };
  const cancelReorder = () => {
    setWidgetStoreState(widgetStores);
    setStatusState("initial");
    setRefreshList(!refreshList);
  };

  /**
   * Control if the stores are in "edit mode" or not
   * @param {*} state
   */
  const handleChangeStatusState = (state) => {
    setStatusState(state);
  };

  /**
   * Refresh the page cleaning the submited query
   */
  const handleRefresh = () => {
    submit(`/actions/refresh/`, {
      method: "GET",
      body: { submited: "" },
    });
  };

  /**
   * Open the edit widget info modal (only for mobile)
   */
  const onOpenEditWidgetInfo = () => {
    setEditWidgetInfoModalOpened(!editWidgetInfoModalOpened);
  };

  /**
   * Control the widget acess
   * @param {*} param0
   * @returns
   */
  const WidgetTogglePush = ({ permission }) => (
    <div className="labeled-switch" sz="large">
      <span>{t("widgets.common.subscriptionsPush")}</span>
      <input
        type="checkbox"
        id="switch-1"
        name="switch-1"
        role="switch"
        onChange={() => {
          const newState = Object.assign({}, formState, {
            pushPermission: !formState.pushPermission,
          });
          setFormState(newState);
          setCurrentCompareState({
            ...compareBase,
            pushPermission: !formState.pushPermission,
          });
        }}
        checked={permission ? true : undefined}
      />
    </div>
  );

  const WidgetTogglePull = ({ permission }) => (
    <div className="labeled-switch" sz="large">
      <span>{t("widgets.common.coachingPull")}</span>
      <input
        type="checkbox"
        id="switch-1"
        name="switch-1"
        role="switch"
        onChange={() => {
          const newState = Object.assign({}, formState, {
            pullPermission: !formState.pullPermission,
          });
          setFormState(newState);
          setCurrentCompareState({
            ...compareBase,
            pullPermission: !formState.pullPermission,
          });
        }}
        checked={permission ? true : undefined}
      />
    </div>
  );

  return (
    <main className="widgets-manager list">
      <WidgetManagerHeader
        name={name}
        description={description}
        goBack={goBack}
        onOpenEditWidgetInfo={onOpenEditWidgetInfo}
      />
      <EditWidgetInfoModal
        widgetName={formState.name}
        widgetDescription={formState.description}
        widgetIcon={formState.icon}
        isOpen={editWidgetInfoModalOpened}
        onClose={onOpenEditWidgetInfo}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        validationSubmit={validationSubmit}
        iconsRadios={WIDGET_ICONS["store"]}
        WidgetTogglePull={WidgetTogglePull}
        WidgetTogglePush={WidgetTogglePush}
        userToRequest={userToRequest}
        pullPermission={pullPermission}
        pushPermission={pushPermission}
      />
      <div>
        {!!activeWidget && (
          <aside className="mobile-hidden">
            <div className="overflow-auto max-h-[calc(100vh-16.75rem)]">
              <label>{t("widgets.common.widgetName")}</label>
              <input
                maxLength="30"
                name="name"
                className="text-input"
                placeholder={t("widgets.common.enterName")}
                value={name}
                onChange={handleInputChange}
              />
              <label>{t("widgets.common.widgetDescription")}</label>
              <textarea
                maxLength="60"
                name="description"
                className="text-input"
                placeholder={t("widgets.common.enterDescriptionHere")}
                value={description}
                onChange={handleInputChange}
              />
              <ImageRadio
                label={t("widgets.common.widgetIcon")}
                name="icon"
                currentValue={icon}
                radios={WIDGET_ICONS["store"]}
                onChange={handleInputChange}
                isManager={true}
              />
              <label>{t("widgets.common.userTypeAccess")}</label>
              {isPullActiveCoach(userToRequest) && (
                <WidgetTogglePull permission={pullPermission} />
              )}
              {activeUser.pushAccountInfo && (
                <WidgetTogglePush permission={pushPermission} />
              )}
            </div>
            <footer>
              <button
                data-button-type="secondary"
                data-action="cancel"
                onClick={deleteWidget}
              >
                {t("widgets.common.delete")}
              </button>
              <button
                data-button-type="primary"
                onClick={handleSubmit}
                disabled={!hasChanged}
              >
                {t("widgets.common.save")}
              </button>
            </footer>
          </aside>
        )}
        <section>
          <header>
            <section className="only-show-mobile back-handler">
              <SvgIcon
                src={"/img/new-icons/back-button.svg"}
                onClickFn={() =>
                  submit(`/actions/refresh/?`, {
                    method: "GET",
                    body: { section: "", learn: "" },
                  })
                }
              />
              <h3>{activeWidget?.name || t("widgets.common.explore")}</h3>
            </section>
            <hgroup className="mobile-hidden">
              <h4>{t("widgets.storeManager.ecommerce")}</h4>
              <p className="p6">{t("widgets.storeManager.description")}</p>
            </hgroup>
            {statusState === "initial" ? (
              <button
                data-button-type="secondary"
                onClick={() => handleChangeStatusState("change-order")}
              >
                {t("widgets.common.changeOrder")}
              </button>
            ) : (
              <hgroup className="actions-wrapper">
                <button
                  data-button-type="primary"
                  onClick={handleSaveReorder}
                  disabled={!changedOrder}
                >
                  {t("widgets.common.save")}
                </button>
                <button
                  data-button-type="secondary"
                  data-action="cancel"
                  onClick={cancelReorder}
                >
                  {t("widgets.common.cancel")}
                </button>
              </hgroup>
            )}
          </header>
          <div className="principal-grabber-div">
            {!!widgetStores &&
              orderWidgets(widgetStoreState).map(
                ({ _id, image, url, widget }) => {
                  return editStoreState !== _id ||
                    statusState === "change-order" ? (
                    <div className="article-wrapper store">
                      <article
                        key={`${_id}-${refreshList}`}
                        id={_id}
                        className={
                          statusState === "change-order"
                            ? "div-recipe columns column item-grabber-container"
                            : "div-recipe columns item-grabber-container"
                        }
                      >
                        <div>
                          <input
                            className="image-section-div"
                            name="image"
                            style={{ display: "none" }}
                            readOnly
                            value={JSON.stringify(image)}
                          />
                          <input
                            className="id-section-div"
                            name="id"
                            style={{ display: "none" }}
                            readOnly
                            value={_id}
                          />
                          <img
                            src={S3.toCDN(image?.s3Path || "")}
                            loading="lazy"
                          />

                          <a
                            style={{ textDecoration: "none" }}
                            target="_blank"
                            rel="noopener"
                            href={url}
                            className="explore-card-title url-section-div"
                          >
                            {url}
                          </a>

                          <div className={`action-buttons`}>
                            <button data-button-type="secondary" data-icon-only>
                              <SvgIcon
                                src={"/img/new-icons/edit-pencil.svg"}
                                onClickFn={() => {
                                  setUrlState({
                                    url: url,
                                    widget: widget,
                                    _id: _id,
                                  });
                                  // handleChangeEditState();
                                  setEditStoreState(_id);
                                  handleRefresh();
                                }}
                              />
                            </button>
                            <button data-button-type="secondary" data-icon-only>
                              <SvgIcon
                                src={"/img/new-icons/red-trash.svg"}
                                onClickFn={(event) => handleDelete(event, _id)}
                                noFilterOnLight={true}
                              />
                            </button>
                          </div>
                        </div>
                      </article>
                      {statusState === "change-order" && (
                        <div className="drag-wrapper">
                          <SvgIcon
                            src="/img/new-icons/menu.svg"
                            className="drag-handle"
                            mobileSrc={"/img/new-icons/drag.svg"}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <EditStore
                      _idProps={_id}
                      urlProps={url}
                      widgetProps={widget}
                      imageProps={image}
                      setEditStoreState={setEditStoreState}
                    />
                  );
                }
              )}
          </div>
          {addNew === "addNewButton" ? (
            <div
              className="dashed-box"
              onClick={() => {
                handleChangeAddNewState();
                handleRefresh();
              }}
            >
              <SvgIcon src="/img/new-icons/add-square-outline.svg" alt="back" />
              <span>{t("widgets.storeManager.addNewStore")}</span>
            </div>
          ) : (
            <EditStore widgetProps={id} setAddNew={setAddNew} />
          )}
        </section>
      </div>
    </main>
  );
};
